import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { newsAPI } from '../utils/api';
import { toast } from 'react-toastify';
import './News.css';

const News = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await newsAPI.getAllNews();
      if (response.success) {
        // Transform backend data to match frontend structure
        const transformedNews = response.news.map(article => {
          let tags = [];

          // Sichere Tag-Verarbeitung
          if (article.tags) {
            try {
              // Versuche JSON zu parsen
              tags = JSON.parse(article.tags);

              // Falls es kein Array ist, zu Array konvertieren
              if (!Array.isArray(tags)) {
                tags = [tags];
              }
            } catch (error) {
              // Falls JSON.parse fehlschlägt, als String-Array verarbeiten
              if (typeof article.tags === 'string') {
                tags = article.tags.split(',').map(tag => tag.trim());
              }
            }
          }

          return {
            id: article.id,
            title: article.title,
            excerpt: article.excerpt || '',
            content: article.content,
            author: article.first_name && article.last_name ? 
              `${article.first_name} ${article.last_name}` : article.username || 'Admin',
            date: article.created_at ? article.created_at.split(' ')[0] : new Date().toISOString().split('T')[0],
            image: article.image_url || '/images/news-placeholder.jpg',
            tags: tags
          };
        });
        setNews(transformedNews);
      }
    } catch (error) {
      console.error('Fehler beim Laden der News:', error);
      toast.error('Fehler beim Laden der News');
      setNews([]); // Show empty state instead of crashing
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Prüfen ob Benutzer angemeldet ist
  if (!user) {
    return (
      <div className="news">
        <div className="container">
          <div className="news-header">
            <h1>News & Aktuelles</h1>
            <p>Exklusiv für EFC Buseck Mitglieder</p>
          </div>

          <div className="login-required" style={{
            textAlign: 'center',
            padding: '60px 20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginTop: '40px'
          }}>
            <h2 style={{ marginBottom: '20px', color: '#333' }}>🔒 Mitgliederbereich</h2>
            <p style={{ marginBottom: '30px', fontSize: '1.1em', color: '#666' }}>
              Der News-Bereich ist nur für angemeldete Mitglieder zugänglich.
            </p>
            <p style={{ marginBottom: '30px', color: '#666' }}>
              Melden Sie sich an, um die neuesten Nachrichten und Updates zu sehen.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/login')}
                className="btn btn-primary"
                style={{ fontSize: '1.1em', padding: '12px 30px' }}
              >
                Jetzt anmelden
              </button>
              <button
                onClick={() => navigate('/registrieren')}
                className="btn btn-secondary"
                style={{ fontSize: '1.1em', padding: '12px 30px' }}
              >
                Registrieren
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <p>News werden geladen...</p>
      </div>
    );
  }

  return (
    <div className="news">
      <div className="container">
        <div className="news-header">
          <h1>News & Aktuelles</h1>
          <p>Bleiben Sie auf dem Laufenden über alle Aktivitäten des EFC Buseck</p>
        </div>

        <div className="news-list">
          {news.map(article => (
            <article key={article.id} className="news-item">
              <div className="news-image">
                <img 
                  src={article.image} 
                  alt={article.title}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDQwMCAyMDAiIGZpbGw9Im5vbGUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjZjhmOWZhIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIwLjNlbSI+QmlsZCBuaWNodCB2ZXJmw7xnYmFyPC90ZXh0Pgo8L3N2Zz4K';
                  }}
                />
              </div>

              <div className="news-content">
                <div className="news-meta">
                  <span className="news-date">{formatDate(article.date)}</span>
                  <span className="news-author">von {article.author}</span>
                </div>

                <h2>{article.title}</h2>

                <p className="news-excerpt">{article.excerpt}</p>

                <div className="news-full-content">
                  <p>{article.content}</p>
                </div>

                <div className="news-tags">
                  {article.tags.map(tag => (
                    <span key={tag} className="news-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="news-footer">
          <div className="news-archive">
            <h3>Ältere News</h3>
            <p>Weitere Artikel finden Sie in unserem Archiv oder folgen Sie uns auf Social Media für tagesaktuelle Updates.</p>
            <div className="social-links">
              <a href="#" className="social-link">Facebook</a>
              <a href="#" className="social-link">Instagram</a>
              <a href="#" className="social-link">Twitter</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default News;
